import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LearnerCourseOverview as Overview, getLearnerCourse } from '../../api/learner';
import { useLearnerSpace } from '../../learner/LearnerSpace';

type Load = { status: 'loading' } | { status: 'error' } | { status: 'ready'; course: Overview };

/**
 * STU-09-FR-06 / STU-14-FR-05 — what a class is about, read-only.
 *
 * It is the course the family enrolled in, read from the course version of
 * the learner's own enrollment (STEP-03, "same offer identity"), not the
 * public course page as it reads today. The public page is a shop window — a
 * price, an enrol button, other teachers' courses — and none of that belongs
 * in the child's space (S07-FIN-01): the server sends only what the class is
 * about, and the way out is back to the learner's own classes.
 */
const LearnerCourseOverview: React.FC = () => {
  const { courseId = '' } = useParams<{ courseId: string }>();
  const { studentId } = useLearnerSpace();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    if (!studentId) return;
    setLoad({ status: 'loading' });
    getLearnerCourse(courseId, studentId)
      .then((course) => setLoad({ status: 'ready', course }))
      .catch(() => setLoad({ status: 'error' }));
  }, [courseId, studentId]);

  return (
    <>
      <p>
        <Link className="lrn-link" to="/student">
          ← Mis clases
        </Link>
      </p>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando…
        </p>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>No pudimos cargar esta clase</h2>
          <p>Prueba otra vez en un momento.</p>
        </div>
      )}

      {load.status === 'ready' && (
        <div className="lrn-status">
          <h1 className="h2">{load.course.title}</h1>
          {load.course.teacherName && <p>Con tu profesor: {load.course.teacherName}</p>}
          {load.course.description && <p>{load.course.description}</p>}
        </div>
      )}
    </>
  );
};

export default LearnerCourseOverview;
